import React, { useState, useEffect } from 'react';
import { Calendar, Download, Mail, Settings, X, Clock, FileText, Eye, Play, Globe, RefreshCw, Plus, CalendarClock } from 'lucide-react';
import { apiCall, apiCallJson, buildApiUrl } from './Api';


const SchedulerWidget = ({ 
  isVisible = true, 
  position = 'bottom-right',
  pageIdentifier = null,
  onClose = null 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [currentUrl, setCurrentUrl] = useState('');
  const [currentParams, setCurrentParams] = useState({});
  const [emailTemplates, setEmailTemplates] = useState([]);

  useEffect(() => {
    const url = window.location.href;
    const urlObj = new URL(url);
    const params = {};
    
    urlObj.searchParams.forEach((value, key) => {
      params[key] = value;
    });

    setCurrentUrl(url);
    setCurrentParams(params);
    loadEmailTemplates();
  }, []);

  const loadEmailTemplates = async () => {
    try {
      const templates = await apiCallJson('/api/scheduler/email-templates');
      setEmailTemplates(Array.isArray(templates) ? templates : []);
    } catch (error) {
      console.error('Error loading email templates:', error);
      setEmailTemplates([]);
    }
  };

  const showToast = (message, type = 'info') => {
    const toast = document.createElement('div');
    toast.className = `fixed top-4 right-4 p-4 rounded-lg z-50 ${
      type === 'success' ? 'bg-green-500' : 
      type === 'error' ? 'bg-red-500' : 'bg-blue-500'
    } text-white max-w-sm`;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 4000);
  };

  const handleImmediateExport = async (format = 'pdf') => {
    setIsExporting(true);
    
    try {
      const exportConfig = {
        export_type: 'route',
        render_url: currentUrl,
        render_params: currentParams,
        filters: currentParams,
        page_identifier: pageIdentifier || window.location.pathname.split('/').pop(),
        output_format: format,
        pdf_orientation: 'portrait',
        pdf_format: 'A4',
        render_wait_time: 10000,
        filename_prefix: `export_${Date.now()}`,
        page_title: document.title || 'Report'
      };

      const response = await apiCall('/api/scheduler/export/sync', {
        method: 'POST',
        body: JSON.stringify(exportConfig),
      });

      if (!response.ok) throw new Error('Export failed');
      
      const result = await response.json();
      
      if (result.download_urls) {
        Object.entries(result.download_urls).forEach(([fileType, url]) => {
          const link = document.createElement('a');
          link.href = buildApiUrl(url);
          link.download = `export_${Date.now()}.${fileType}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        });
        showToast('Export completed and downloaded!', 'success');
      }
      
      setIsOpen(false);
    } catch (error) {
      showToast('Export failed: ' + error.message, 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const positionClasses = {
    'bottom-right': 'bottom-6 right-6',
    'bottom-left': 'bottom-6 left-6',
    'top-right': 'top-6 right-6',
    'top-left': 'top-6 left-6',
    'static': ''
  };

  if (!isVisible) return null;

  const containerClass = position === 'static' 
    ? 'relative' 
    : `fixed ${positionClasses[position]} z-40`;

  return (
    <>
      <div className={containerClass}>
        {/* Main Widget Button */}
        {!isOpen && (
          <button
            onClick={() => setIsOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white w-12 h-12 rounded-full shadow-lg transition-all duration-200 hover:scale-105 flex items-center justify-center"
            title="Schedule Reports"
          >
            <CalendarClock className="w-5 h-5" />
          </button>
        )}

        {/* Expanded Widget Panel - adjust positioning for static */}
        {isOpen && (
          <div className={`bg-white rounded-lg shadow-xl border border-gray-200 p-4 w-80 ${
            position === 'static' 
              ? 'absolute bottom-full right-0 mb-2' 
              : ''
          }`}>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-600" />
                <h3 className="font-medium text-gray-900">Export Now</h3>
              </div>
              <button
                onClick={() => {
                  setIsOpen(false);
                  onClose?.();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Current Page Info */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="text-sm font-medium text-gray-700 mb-1">Current Page:</div>
              <div className="text-xs text-gray-600 break-all">{document.title || 'Dashboard'}</div>
              <div className="text-xs text-blue-600 mt-1">{currentUrl}</div>
              {Object.keys(currentParams).length > 0 && (
                <div className="text-xs text-green-600 mt-1">
                  {Object.keys(currentParams).length} parameter(s) captured
                </div>
              )}
            </div>

            {/* Quick Export Buttons */}
            <div className="space-y-3 mb-4">
              <h4 className="text-sm font-medium text-gray-700">Export Now:</h4>
              
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => handleImmediateExport('pdf')}
                  disabled={isExporting}
                  className="flex items-center justify-center gap-2 bg-red-50 text-red-700 p-3 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                >
                  <Download className="w-4 h-4" />
                  PDF
                </button>
                
                <button
                  onClick={() => handleImmediateExport('png')}
                  disabled={isExporting}
                  className="flex items-center justify-center gap-2 bg-green-50 text-green-700 p-3 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
                >
                  <Download className="w-4 h-4" />
                  PNG
                </button>
              </div>

              <button
                onClick={() => handleImmediateExport('both')}
                disabled={isExporting}
                className="w-full flex items-center justify-center gap-2 bg-blue-50 text-blue-700 p-3 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                Both PDF & PNG
              </button>
            </div>

            {/* Schedule Button */}
            <div className="border-t pt-3">
              <button
                onClick={() => setShowScheduleModal(true)}
                className="w-full flex items-center justify-center gap-2 bg-purple-50 text-purple-700 p-3 rounded-lg hover:bg-purple-100 transition-colors"
              >
                <Calendar className="w-4 h-4" />
                Schedule This Dashboard
              </button>
            </div>

            {/* Footer */}
            <div className="mt-4 pt-3 border-t text-center">
              <button 
                onClick={() => window.open('/scheduler', '_blank')}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Open Full Scheduler →
              </button>
            </div>
          </div>
        )}

        {/* Loading Overlay */}
        {isExporting && (
          <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-lg">
            <div className="flex items-center gap-2 text-blue-600">
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span className="text-sm">Exporting...</span>
            </div>
          </div>
        )}
      </div>

      {/* Schedule Configuration Modal */}
      <ScheduleModal 
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        currentUrl={currentUrl}
        currentParams={currentParams}
        pageIdentifier={pageIdentifier}
        emailTemplates={emailTemplates}
        showToast={showToast}
      />
    </>
  );
};

// Separate Schedule Modal Component with Full Configuration
const ScheduleModal = ({ isOpen, onClose, currentUrl, currentParams, pageIdentifier, emailTemplates, showToast }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    template_id: '',
    recipients: '',
    cc_recipients: '',
    bcc_recipients: '',
    cron_expression: '0 8 * * *',
    timezone: 'UTC',
    render_wait_time: 5000,
    date_range_type: 'relative',
    date_from: '',
    date_to: '',
    relative_days: 1,
    output_format: 'pdf',
    pdf_orientation: 'portrait',
    pdf_format: 'A4',
    page_title: '',
    is_active: true
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Pre-populate form with current page data
      setFormData(prev => ({
        ...prev,
        name: `Scheduled Report: ${document.title || 'Dashboard'}`,
        description: `Automated report for ${currentUrl}`,
        page_title: document.title || 'Dashboard Report',
        template_id: emailTemplates.length > 0 ? emailTemplates[0].id : ''
      }));
    }
  }, [isOpen, currentUrl, emailTemplates]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const reportData = {
        ...formData,
        export_type: 'route',
        render_url: currentUrl,
        render_params: currentParams,
        filters: currentParams,
        page_identifier: pageIdentifier || window.location.pathname.split('/').pop(),
        recipients: formData.recipients.split(',').map(email => email.trim()).filter(Boolean),
        cc_recipients: formData.cc_recipients ? formData.cc_recipients.split(',').map(email => email.trim()).filter(Boolean) : [],
        bcc_recipients: formData.bcc_recipients ? formData.bcc_recipients.split(',').map(email => email.trim()).filter(Boolean) : [],
        query_template: null,
        query_parameters: {},
        visualization_config: '{}',
        created_by: 'widget_user'
      };

      const response = await apiCall('/api/scheduler/scheduled-reports', {
        method: 'POST',
        body: JSON.stringify(reportData),
      });

      if (!response.ok) throw new Error('Failed to create scheduled report');
      
      const result = await response.json();
      showToast(`Scheduled report created successfully! ID: ${result.id}`, 'success');
      onClose();
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        template_id: '',
        recipients: '',
        cc_recipients: '',
        bcc_recipients: '',
        cron_expression: '0 8 * * *',
        timezone: 'UTC',
        render_wait_time: 5000,
        date_range_type: 'relative',
        date_from: '',
        date_to: '',
        relative_days: 1,
        output_format: 'pdf',
        pdf_orientation: 'portrait',
        pdf_format: 'A4',
        page_title: '',
        is_active: true
      });
      
    } catch (error) {
      showToast('Failed to create scheduled report: ' + error.message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium text-gray-900">Schedule Page Report</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Current Page Info */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Page to Schedule:</h4>
            <div className="text-sm text-blue-800">
              <div className="font-medium">{document.title || 'Dashboard'}</div>
              <div className="text-xs mt-1 break-all">{currentUrl}</div>
              {Object.keys(currentParams).length > 0 && (
                <div className="text-xs mt-2">
                  <strong>Parameters:</strong> {Object.entries(currentParams).map(([key, value]) => `${key}=${value}`).join(', ')}
                </div>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 border-b pb-2">Basic Information</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Report Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter report name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="2"
                  placeholder="Enter report description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Template *</label>
                  <select
                    required
                    value={formData.template_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, template_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select template</option>
                    {emailTemplates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Page Title</label>
                  <input
                    type="text"
                    value={formData.page_title}
                    onChange={(e) => setFormData(prev => ({ ...prev, page_title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Report title for email"
                  />
                </div>
              </div>
            </div>

            {/* Email Recipients */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 border-b pb-2">Email Recipients</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Recipients * (comma-separated)</label>
                <input
                  type="text"
                  required
                  value={formData.recipients}
                  onChange={(e) => setFormData(prev => ({ ...prev, recipients: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="email1@example.com, email2@example.com"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CC Recipients</label>
                  <input
                    type="text"
                    value={formData.cc_recipients}
                    onChange={(e) => setFormData(prev => ({ ...prev, cc_recipients: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="cc1@example.com, cc2@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">BCC Recipients</label>
                  <input
                    type="text"
                    value={formData.bcc_recipients}
                    onChange={(e) => setFormData(prev => ({ ...prev, bcc_recipients: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="bcc1@example.com, bcc2@example.com"
                  />
                </div>
              </div>
            </div>

            {/* Schedule Configuration */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 border-b pb-2">Schedule Configuration</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Schedule *</label>
                  <select
                    value={formData.cron_expression}
                    onChange={(e) => setFormData(prev => ({ ...prev, cron_expression: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="0 8 * * *">Daily at 8:00 AM</option>
                    <option value="0 9 * * 1">Weekly on Monday at 9:00 AM</option>
                    <option value="0 0 1 * *">Monthly on 1st at midnight</option>
                    <option value="0 18 * * 5">Weekly on Friday at 6:00 PM</option>
                    <option value="0 6 * * *">Daily at 6:00 AM</option>
                    <option value="0 12 * * *">Daily at 12:00 PM</option>
                    <option value="0 0 * * 1">Weekly on Monday at midnight</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                  <select
                    value={formData.timezone}
                    onChange={(e) => setFormData(prev => ({ ...prev, timezone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">Eastern Time</option>
                    <option value="America/Chicago">Central Time</option>
                    <option value="America/Denver">Mountain Time</option>
                    <option value="America/Los_Angeles">Pacific Time</option>
                    <option value="Europe/London">London</option>
                    <option value="Europe/Paris">Paris</option>
                    <option value="Asia/Tokyo">Tokyo</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Date Range Configuration */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 border-b pb-2">Date Range Configuration</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date Range Type</label>
                <select
                  value={formData.date_range_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, date_range_type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="relative">Relative (last N days)</option>
                  <option value="fixed">Fixed date range</option>
                  <option value="dynamic">Dynamic (current period)</option>
                </select>
              </div>

              {formData.date_range_type === 'relative' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Number of Days</label>
                  <select
                    value={formData.relative_days}
                    onChange={(e) => setFormData(prev => ({ ...prev, relative_days: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="1">Last 1 day</option>
                    <option value="7">Last 7 days</option>
                    <option value="30">Last 30 days</option>
                    <option value="90">Last 90 days</option>
                  </select>
                </div>
              )}

              {formData.date_range_type === 'fixed' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                    <input
                      type="date"
                      value={formData.date_from}
                      onChange={(e) => setFormData(prev => ({ ...prev, date_from: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                    <input
                      type="date"
                      value={formData.date_to}
                      onChange={(e) => setFormData(prev => ({ ...prev, date_to: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Output Configuration */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 border-b pb-2">Output Configuration</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Output Format</label>
                  <select
                    value={formData.output_format}
                    onChange={(e) => setFormData(prev => ({ ...prev, output_format: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="pdf">PDF</option>
                    <option value="png">PNG</option>
                    <option value="both">Both PDF & PNG</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Wait Time (ms)</label>
                  <select
                    value={formData.render_wait_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, render_wait_time: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="3000">3 seconds</option>
                    <option value="5000">5 seconds</option>
                    <option value="10000">10 seconds</option>
                    <option value="15000">15 seconds</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PDF Orientation</label>
                  <select
                    value={formData.pdf_orientation}
                    onChange={(e) => setFormData(prev => ({ ...prev, pdf_orientation: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="portrait">Portrait</option>
                    <option value="landscape">Landscape</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PDF Format</label>
                  <select
                    value={formData.pdf_format}
                    onChange={(e) => setFormData(prev => ({ ...prev, pdf_format: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="A4">A4</option>
                    <option value="A3">A3</option>
                    <option value="letter">Letter</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="mr-2"
                />
                <label htmlFor="is_active" className="text-sm text-gray-700">
                  Activate schedule immediately
                </label>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-6 border-t">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Schedule Report
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SchedulerWidget;